import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

import { api } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { cn } from '../lib/cn';
import { formatCount, formatCurrency, formatDateTime } from '../lib/format';
import { EmptyState, ErrorNote, Panel, SearchBox, Spinner, TenderChips } from '../components/ui';
import { RangePicker } from '../components/RangePicker';
import { preset, rangeKey, rangeQuery, type RangeValue } from '../lib/range';
import { useDebounced } from '../lib/useDebounced';
import {
  DocumentViewer,
  ReceiptButton,
  type DocumentRequest,
} from '../components/DocumentViewer';

interface Row {
  id: number;
  at: string;
  total: string;
  tillName: string;
  userName: string;
  itemCount: number;
  methods: string[];
}

/**
 * Sales and refunds.
 *
 * Two tabs rather than one merged list, and the reason is not layout. A refund
 * is not a negative sale: `SalesReturn` carries no `saleid`, so a return cannot
 * be shown against the sale it reverses, and interleaving the two would produce
 * a ledger that looks like it reconciles and does not. Separate lists are
 * honest about what the till actually recorded.
 *
 * Refund totals are still rendered negative, because the money left the drawer
 * and a column of positive numbers under a heading that says "Refunds" is a trap
 * for anyone scanning quickly. The API returns them positive — `ReturnTotal` is
 * stored positive and every reporting query sums it that way — so the sign is a
 * display decision made here, once.
 */
const TABS = [
  { key: 'sales', label: 'Sales', path: 'sales', noun: 'sale' },
  { key: 'returns', label: 'Refunds', path: 'sale-returns', noun: 'refund' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

export function Sales(): React.JSX.Element {
  const { activeStore } = useAuth();
  const storeId = activeStore?.storeId;
  const [range, setRange] = useState<RangeValue>(preset('last30'));
  const [tab, setTab] = useState<TabKey>('sales');
  const [search, setSearch] = useState('');
  const applied = useDebounced(search);
  const [viewing, setViewing] = useState<DocumentRequest | null>(null);

  const active = TABS.find((option) => option.key === tab)!;
  const isReturns = tab === 'returns';

  const { data, error, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['trade', storeId, active.path, rangeKey(range), applied],
      queryFn: ({ pageParam }) =>
        api.get<{ items: unknown[]; nextCursor: string | null }>(
          `/stores/${storeId}/${active.path}?${rangeQuery(range)}&limit=50` +
            `${applied ? `&search=${encodeURIComponent(applied)}` : ''}` +
            `${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
        ),
      initialPageParam: '',
      getNextPageParam: (last) => last.nextCursor ?? undefined,
      enabled: Boolean(storeId),
    });

  // The two endpoints differ only in what they call the identifier.
  const rows: Row[] = (data?.pages.flatMap((page) => page.items) ?? []).map((item) => {
    const row = item as Record<string, unknown> & Omit<Row, 'id'>;
    return { ...row, id: Number(row['saleId'] ?? row['returnId']) };
  });

  const timeZone = activeStore?.timezone ?? 'Europe/London';

  return (
    <>
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="qs-eyebrow">Transactions</p>
          <h1 className="text-lg font-medium tracking-tight">Sales</h1>
        </div>
        <RangePicker value={range} onChange={setRange} />
      </header>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex gap-1" role="group" aria-label="Sales or refunds">
        {TABS.map((option) => (
          <button
            key={option.key}
            type="button"
            onClick={() => setTab(option.key)}
            aria-pressed={tab === option.key}
            className={cn(
              'rounded border px-2.5 py-1 text-xs transition-colors',
              tab === option.key
                ? 'border-accent bg-accent-soft font-medium text-accent'
                : 'border-rule text-ink-muted hover:border-rule-strong hover:text-ink',
            )}
          >
            {option.label}
          </button>
        ))}
        </div>

        {/* One box for both tabs: the filter is a property of what you are
            looking for, not of which list you are looking at. */}
        <SearchBox
          value={search}
          onChange={setSearch}
          label={`Find a ${active.noun} by product`}
          className="max-w-72 flex-1"
        />
      </div>

      {error && <ErrorNote error={error} />}

      <Panel>
        {isPending ? (
          <Spinner label={`Loading ${active.label.toLowerCase()}…`} />
        ) : rows.length === 0 ? (
          <EmptyState>
            {applied
              ? `No ${active.noun} in this period contains a product matching “${applied}”.`
              : `No ${active.noun}s in this period.`}
          </EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <caption className="sr-only">{active.label}</caption>
              <thead>
                <tr className="border-b border-rule">
                  <th className="qs-eyebrow py-2 pl-3 text-left">
                    {isReturns ? 'Refund' : 'Receipt'}
                  </th>
                  <th className="qs-eyebrow py-2 text-left">When</th>
                  <th className="qs-eyebrow hidden py-2 text-left md:table-cell">Till</th>
                  <th className="qs-eyebrow hidden py-2 text-left md:table-cell">Served by</th>
                  <th className="qs-eyebrow hidden py-2 text-left lg:table-cell">
                    {isReturns ? 'Refunded to' : 'Tender'}
                  </th>
                  <th className="qs-eyebrow hidden py-2 text-right lg:table-cell">Items</th>
                  <th className="qs-eyebrow py-2 pr-3 text-right">Total</th>
                  <th className="py-2 pr-3">
                    <span className="sr-only">Receipt</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b border-rule last:border-0">
                    <td className="qs-figure py-1.5 pl-3 text-xs">#{row.id}</td>
                    <td className="qs-figure py-1.5 text-xs text-ink-muted">
                      {formatDateTime(row.at, timeZone)}
                    </td>
                    <td className="hidden py-1.5 text-xs text-ink-muted md:table-cell">
                      {row.tillName}
                    </td>
                    <td className="hidden py-1.5 text-xs text-ink-muted md:table-cell">
                      {row.userName}
                    </td>
                    <td className="hidden py-1.5 lg:table-cell">
                      <TenderChips methods={row.methods} />
                    </td>
                    <td className="qs-figure hidden py-1.5 text-right text-xs lg:table-cell">
                      {formatCount(row.itemCount)}
                    </td>
                    <td
                      className={cn(
                        'qs-figure py-1.5 pr-3 text-right text-xs',
                        isReturns && 'text-down',
                      )}
                    >
                      {isReturns ? `-${formatCurrency(row.total)}` : formatCurrency(row.total)}
                    </td>
                    <td className="py-1.5 pr-3 text-right">
                      <ReceiptButton
                        label={`Receipt for ${active.noun} #${row.id}`}
                        onOpen={() =>
                          setViewing({
                            path: `/stores/${storeId}/documents/${
                              isReturns ? 'return' : 'sale'
                            }/${row.id}`,
                            title: `${isReturns ? 'Refund' : 'Sale'} #${row.id}`,
                            formats: ['text'],
                          })
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {hasNextPage && (
          <div className="border-t border-rule p-2 text-center">
            <button
              type="button"
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage}
              className="rounded border border-rule px-3 py-1.5 text-xs text-ink-muted hover:border-rule-strong hover:text-ink disabled:opacity-60"
            >
              {isFetchingNextPage ? 'Loading…' : `Load more (${rows.length} shown)`}
            </button>
          </div>
        )}
      </Panel>

      <DocumentViewer request={viewing} onClose={() => setViewing(null)} />
    </>
  );
}
